import type { SupportedLocale } from '@erp/shared';

export const mailSettingsTranslations = {
  de: {
    title: 'E-Mail-Versand und SMTP',
    scope: 'Diese Einstellungen gelten ausschließlich für den aktuellen Mandanten.',
    help: 'Hilfe zu E-Mail und SMTP',
    enabled: 'E-Mail-Versand für diesen Mandanten erlauben',
    blockHint:
      'Ausschalten sperrt sämtliche E-Mails dieses Mandanten, einschließlich Belegen, Newslettern, Testmails, Sicherheitscodes und Betriebsalarmen. E-Mail-Anmeldungen und Passwortzurücksetzungen sind dann nicht verfügbar. Bereits übergebene Nachrichten können nicht zurückgerufen werden.',
    useCustomSmtp: 'Eigenen SMTP-Server verwenden',
    inherited:
      'Ohne eigenen SMTP-Server gelten die Servervorgaben. Andere Mandanten bleiben unverändert.',
    host: 'SMTP-Host',
    port: 'SMTP-Port',
    username: 'SMTP-Benutzername',
    password: 'SMTP-Passwort',
    fromName: 'Absendername',
    fromAddress: 'Absenderadresse',
    secure: 'Direkte TLS-Verbindung (üblicherweise Port 465)',
    clearPassword: 'Gespeichertes SMTP-Passwort löschen',
    passwordHint:
      'Leer lassen behält das Passwort nur bei unverändertem Host, Port, TLS-Modus und Benutzernamen. Bei einer Änderung ein neues Passwort eingeben.',
    passwordStored:
      'Ein verschlüsseltes Passwort für die bisherige SMTP-Verbindung ist gespeichert.',
    active: 'Versand erlaubt',
    blocked: 'Versand gesperrt',
    save: 'E-Mail-Einstellungen speichern',
    saving: 'Wird gespeichert …',
    saved: 'E-Mail-Einstellungen gespeichert',
    loadError: 'E-Mail-Einstellungen konnten nicht geladen werden.',
    retry: 'Erneut versuchen',
    invalid: 'Prüfen Sie die markierten Felder. Ihre Eingaben bleiben erhalten.',
    invalidField: 'Bitte einen gültigen Wert eingeben.',
    deliveryDisabled: 'Der E-Mail-Versand ist für diesen Mandanten gesperrt.',
    conflict:
      'Die Einstellungen wurden zwischenzeitlich geändert. Ihre Eingaben bleiben erhalten. Öffnen Sie die Seite erneut, um den aktuellen Stand zu laden.',
    credentialsRequired:
      'SMTP-Benutzername und Passwort müssen gemeinsam angegeben werden. Bei einer geänderten Verbindung muss das Passwort neu eingegeben werden.',
  },
  en: {
    title: 'Email delivery and SMTP',
    scope: 'These settings apply only to the current tenant.',
    help: 'Email and SMTP help',
    enabled: 'Allow email delivery for this tenant',
    blockHint:
      'Switching off blocks all emails for this tenant, including documents, newsletters, test emails, security codes and operations alerts. Email sign-in and password resets will be unavailable. Messages already handed over cannot be recalled.',
    useCustomSmtp: 'Use a custom SMTP server',
    inherited:
      'Without a custom SMTP server, infrastructure defaults apply. Other tenants are unaffected.',
    host: 'SMTP host',
    port: 'SMTP port',
    username: 'SMTP username',
    password: 'SMTP password',
    fromName: 'Sender name',
    fromAddress: 'Sender address',
    secure: 'Direct TLS connection (usually port 465)',
    clearPassword: 'Delete stored SMTP password',
    passwordHint:
      'Leave blank to retain the password only when host, port, TLS mode and username are unchanged. Enter a new password when changing them.',
    passwordStored: 'An encrypted password is stored for the previous SMTP connection.',
    active: 'Delivery allowed',
    blocked: 'Delivery blocked',
    save: 'Save email settings',
    saving: 'Saving …',
    saved: 'Email settings saved',
    loadError: 'Email settings could not be loaded.',
    retry: 'Retry',
    invalid: 'Check the highlighted fields. Your inputs are retained.',
    invalidField: 'Enter a valid value.',
    deliveryDisabled: 'Email delivery is blocked for this tenant.',
    conflict:
      'The settings have changed. Your inputs are retained. Reopen this page to load the current settings.',
    credentialsRequired:
      'Provide SMTP username and password together. A changed connection requires the password to be entered again.',
  },
  fr: {
    title: 'Envoi des e-mails et SMTP',
    scope: 'Ces paramètres concernent uniquement l’entreprise actuelle.',
    help: 'Aide e-mail et SMTP',
    enabled: 'Autoriser les e-mails pour cette entreprise',
    blockHint:
      'La désactivation bloque tous les e-mails de cette entreprise : documents, newsletters, tests, codes de sécurité et alertes. La connexion par e-mail et la réinitialisation des mots de passe deviennent indisponibles. Les messages déjà transmis ne peuvent pas être rappelés.',
    useCustomSmtp: 'Utiliser un serveur SMTP personnalisé',
    inherited:
      'Sans serveur personnalisé, les paramètres du serveur s’appliquent. Les autres entreprises restent inchangées.',
    host: 'Hôte SMTP',
    port: 'Port SMTP',
    username: 'Identifiant SMTP',
    password: 'Mot de passe SMTP',
    fromName: 'Nom de l’expéditeur',
    fromAddress: 'Adresse de l’expéditeur',
    secure: 'Connexion TLS directe (généralement port 465)',
    clearPassword: 'Supprimer le mot de passe SMTP enregistré',
    passwordHint:
      'Un champ vide conserve le mot de passe uniquement si hôte, port, mode TLS et identifiant restent identiques. Sinon, saisissez à nouveau le mot de passe.',
    passwordStored: 'Un mot de passe chiffré est enregistré pour la connexion SMTP précédente.',
    active: 'Envoi autorisé',
    blocked: 'Envoi bloqué',
    save: 'Enregistrer les paramètres e-mail',
    saving: 'Enregistrement …',
    saved: 'Paramètres e-mail enregistrés',
    loadError: 'Impossible de charger les paramètres e-mail.',
    retry: 'Réessayer',
    invalid: 'Vérifiez les champs signalés. Vos saisies sont conservées.',
    invalidField: 'Saisissez une valeur valide.',
    deliveryDisabled: 'L’envoi des e-mails est bloqué pour cette entreprise.',
    conflict:
      'Les paramètres ont changé. Vos saisies sont conservées. Rouvrez cette page pour charger les paramètres actuels.',
    credentialsRequired:
      'Renseignez identifiant et mot de passe ensemble. Une connexion modifiée exige une nouvelle saisie du mot de passe.',
  },
  es: {
    title: 'Envío de correo y SMTP',
    scope: 'Estos ajustes solo se aplican a la empresa actual.',
    help: 'Ayuda de correo y SMTP',
    enabled: 'Permitir el envío de correo de esta empresa',
    blockHint:
      'Al desactivar se bloquean todos los correos de esta empresa: documentos, boletines, pruebas, códigos de seguridad y alertas. El acceso por correo y el restablecimiento de contraseñas no estarán disponibles. Los mensajes ya entregados no se pueden retirar.',
    useCustomSmtp: 'Usar un servidor SMTP propio',
    inherited:
      'Sin servidor propio se usan los ajustes del servidor. Las demás empresas no se ven afectadas.',
    host: 'Servidor SMTP',
    port: 'Puerto SMTP',
    username: 'Usuario SMTP',
    password: 'Contraseña SMTP',
    fromName: 'Nombre del remitente',
    fromAddress: 'Dirección del remitente',
    secure: 'Conexión TLS directa (normalmente puerto 465)',
    clearPassword: 'Eliminar la contraseña SMTP guardada',
    passwordHint:
      'Dejar vacío conserva la contraseña solo si servidor, puerto, modo TLS y usuario no cambian. Si cambian, introduzca de nuevo la contraseña.',
    passwordStored: 'Hay una contraseña cifrada guardada para la conexión SMTP anterior.',
    active: 'Envío permitido',
    blocked: 'Envío bloqueado',
    save: 'Guardar ajustes de correo',
    saving: 'Guardando …',
    saved: 'Ajustes de correo guardados',
    loadError: 'No se pudieron cargar los ajustes de correo.',
    retry: 'Reintentar',
    invalid: 'Revise los campos marcados. Se conservan sus datos.',
    invalidField: 'Introduzca un valor válido.',
    deliveryDisabled: 'El envío de correo está bloqueado para esta empresa.',
    conflict:
      'Los ajustes han cambiado. Se conservan sus datos. Vuelva a abrir la página para cargar los ajustes actuales.',
    credentialsRequired:
      'Indique usuario y contraseña juntos. Si cambia la conexión, debe introducir la contraseña de nuevo.',
  },
  tr: {
    title: 'E-posta gönderimi ve SMTP',
    scope: 'Bu ayarlar yalnızca geçerli şirket için uygulanır.',
    help: 'E-posta ve SMTP yardımı',
    enabled: 'Bu şirket için e-posta gönderimine izin ver',
    blockHint:
      'Kapatıldığında belgeler, bültenler, test iletileri, güvenlik kodları ve işletim uyarıları dahil bu şirketin tüm e-postaları engellenir. E-posta ile giriş ve parola sıfırlama kullanılamaz. Önceden teslim edilen iletiler geri alınamaz.',
    useCustomSmtp: 'Özel SMTP sunucusu kullan',
    inherited:
      'Özel sunucu kullanılmadığında sunucu varsayılanları geçerlidir. Diğer şirketler etkilenmez.',
    host: 'SMTP sunucusu',
    port: 'SMTP bağlantı noktası',
    username: 'SMTP kullanıcı adı',
    password: 'SMTP parolası',
    fromName: 'Gönderen adı',
    fromAddress: 'Gönderen adresi',
    secure: 'Doğrudan TLS bağlantısı (genellikle 465 numaralı bağlantı noktası)',
    clearPassword: 'Kaydedilmiş SMTP parolasını sil',
    passwordHint:
      'Boş alan parolayı yalnızca sunucu, bağlantı noktası, TLS modu ve kullanıcı adı aynıysa korur. Değişiklikte parolayı yeniden girin.',
    passwordStored: 'Önceki SMTP bağlantısı için şifrelenmiş parola kayıtlı.',
    active: 'Gönderime izin veriliyor',
    blocked: 'Gönderim engellendi',
    save: 'E-posta ayarlarını kaydet',
    saving: 'Kaydediliyor …',
    saved: 'E-posta ayarları kaydedildi',
    loadError: 'E-posta ayarları yüklenemedi.',
    retry: 'Yeniden dene',
    invalid: 'İşaretli alanları kontrol edin. Girdileriniz korunur.',
    invalidField: 'Geçerli bir değer girin.',
    deliveryDisabled: 'Bu şirket için e-posta gönderimi engellendi.',
    conflict:
      'Ayarlar değişti. Girdileriniz korunur. Güncel ayarları yüklemek için sayfayı yeniden açın.',
    credentialsRequired:
      'SMTP kullanıcı adı ve parolasını birlikte girin. Bağlantı değiştiğinde parola yeniden girilmelidir.',
  },
} satisfies Record<SupportedLocale, Record<string, string>>;
